import { useMemo, useRef, useState } from 'react';

import { getClubsApi } from '@/lib/api/clubsApi';
import {
  addOrgAdminApi,
  attachClubApi,
  deleteOrganizationApi,
  detachClubApi,
  getOrgAdminRequestsApi,
  getOrganizationApi,
  pingOrgAdminsApi,
  removeOrgAdminApi,
  updateOrgAdminRequestApi,
  updateOrganizationApi,
} from '@/lib/api/organizationsApi';
import { getUsersApi } from '@/lib/api/usersApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { formatDateAbbreviatedFr, getNameInitials } from '@shared';
import {
  OrgAdminRole,
  OrganizationAdminRequestStatus,
  StripeAccountStatus,
  SubPlan,
  SubscriptionStatus,
} from '@shared/enums/domain.enum';
import type { AuthUser, Club, Organization, OrganizationAdminRequest } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  Briefcase,
  Building,
  CreditCard,
  ExternalLink,
  Loader2,
  Search,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

import ConfirmModal from '../ui/ConfirmModal';
import CmsOfferControl from './CmsOfferControl';

interface OrganizerDetailDrawerProps {
  orgId: number | null;
  onClose: () => void;
  frontUrl: string;
}

type TabKey = 'infos' | 'edit' | 'admins' | 'clubs' | 'requests';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'infos', label: 'Infos' },
  { key: 'edit', label: 'Édition' },
  { key: 'admins', label: 'Admins' },
  { key: 'clubs', label: 'Clubs' },
  { key: 'requests', label: 'Demandes' },
];

// ─── Badges ────────────────────────────────────────────────────────

function subStatusLabel(status: SubscriptionStatus): { label: string; cls: string } {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return { label: 'Actif', cls: 'bg-emerald-100 text-emerald-700' };
    case SubscriptionStatus.PAST_DUE:
      return { label: 'Retard CB', cls: 'bg-red-100 text-red-700' };
    case SubscriptionStatus.UNPAID:
      return { label: 'Impayé', cls: 'bg-red-100 text-red-700' };
    default:
      return { label: 'Résilié', cls: 'bg-slate-100 text-slate-500' };
  }
}

function connectLabel(org: Organization): { label: string; cls: string } | null {
  if (!org.stripeConnectAccountId) return null;
  switch (org.stripeAccountStatus) {
    case StripeAccountStatus.ACTIVE:
      return { label: 'Connect actif', cls: 'bg-emerald-100 text-emerald-700' };
    case StripeAccountStatus.ONBOARDING:
      return { label: 'Onboarding', cls: 'bg-blue-100 text-blue-700' };
    case StripeAccountStatus.RESTRICTED:
      return { label: 'Restreint', cls: 'bg-amber-100 text-amber-700' };
    case StripeAccountStatus.DISABLED:
      return { label: 'Désactivé', cls: 'bg-red-100 text-red-700' };
    default:
      return { label: 'Connect', cls: 'bg-slate-100 text-slate-500' };
  }
}

const ORG_ADMIN_ROLE_LABELS: Record<OrgAdminRole, string> = {
  [OrgAdminRole.OWNER]: 'Propriétaire',
  [OrgAdminRole.ADMIN]: 'Admin',
};

// ─── Edit form ─────────────────────────────────────────────────────

interface EditForm {
  name: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  city: string;
  postcode: string;
  department: string;
  region: string;
  siren: string;
  isProfilePublic: boolean;
}

function toEditForm(org: Organization): EditForm {
  return {
    name: org.name ?? '',
    email: org.email ?? '',
    phone: org.phone ?? '',
    website: org.website ?? '',
    address: org.address ?? '',
    city: org.city ?? '',
    postcode: org.postcode ?? '',
    department: org.department ?? '',
    region: org.region ?? '',
    siren: org.siren ?? '',
    isProfilePublic: org.isProfilePublic,
  };
}

// ─── Component ─────────────────────────────────────────────────────

export default function OrganizerDetailDrawer({ orgId, onClose, frontUrl }: OrganizerDetailDrawerProps) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('infos');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [adminToRemove, setAdminToRemove] = useState<{ userId: number; name: string } | null>(null);
  const [clubToDetach, setClubToDetach] = useState<{ id: number; name: string } | null>(null);

  // Reset to the first tab whenever a different org is opened — store-previous-prop pattern
  // (React docs: adjust state during render, avoids an effect + cascading render).
  const [prevOrgId, setPrevOrgId] = useState<number | null>(null);
  if (orgId !== null && orgId !== prevOrgId) {
    setPrevOrgId(orgId);
    setTab('infos');
  }

  const open = orgId !== null;

  const { data: org, isLoading } = useQuery({
    queryKey: queryKeys.organizations.detail(orgId ?? 0),
    queryFn: () => getOrganizationApi(orgId as number),
    enabled: open,
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: queryKeys.organizations.all() });
    if (orgId) void qc.invalidateQueries({ queryKey: queryKeys.organizations.detail(orgId) });
  }

  const deleteMutation = useMutation({
    mutationFn: () => deleteOrganizationApi(orgId as number),
    onSuccess: () => {
      toast.success('Organisation supprimée');
      setConfirmDelete(false);
      invalidate();
      onClose();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors de la suppression'),
  });

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onClose}
      />

      {/* Panel */}
      <aside
        className={`fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!open}
      >
        {/* Header */}
        <div className='flex items-start gap-3 border-b border-slate-200 p-5'>
          {org && (
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-orange-600 text-sm font-bold text-white'>
              {getNameInitials(org.name)}
            </div>
          )}
          <div className='min-w-0 flex-1'>
            <h2 className='truncate text-lg font-bold text-slate-900'>{org?.name ?? 'Chargement…'}</h2>
            {org && <p className='truncate text-xs font-mono text-slate-400'>#{org.alias}</p>}
          </div>
          <a
            href={`${frontUrl}/organisateurs/${orgId}`}
            target='_blank'
            rel='noreferrer'
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-blue-600'
            title='Voir le profil public'
          >
            <ExternalLink className='h-4 w-4' />
          </a>
          <button
            onClick={onClose}
            className='rounded-lg p-2 text-slate-400 transition hover:bg-slate-100'
            aria-label='Fermer'
          >
            <X className='h-5 w-5' />
          </button>
        </div>

        {/* Tabs */}
        <div className='flex gap-1 border-b border-slate-200 px-5'>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold transition ${
                tab === t.key
                  ? 'border-amber-600 text-amber-700'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className='flex-1 overflow-y-auto p-5'>
          {isLoading || !org ? (
            <div className='flex h-40 items-center justify-center text-slate-400'>
              <Loader2 className='h-6 w-6 animate-spin' />
            </div>
          ) : (
            <>
              {tab === 'infos' && <InfosTab org={org} />}
              {tab === 'edit' && <EditTab org={org} onSaved={invalidate} />}
              {tab === 'admins' && <AdminsTab org={org} onChanged={invalidate} onAskRemove={setAdminToRemove} />}
              {tab === 'clubs' && <ClubsTab org={org} onChanged={invalidate} onAskDetach={setClubToDetach} />}
              {tab === 'requests' && <RequestsTab orgId={org.id} onAdminChanged={invalidate} />}
            </>
          )}
        </div>

        {/* Danger zone footer */}
        {org && (
          <div className='border-t border-slate-200 p-4'>
            <button
              onClick={() => setConfirmDelete(true)}
              className='flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50'
            >
              <Trash2 className='h-4 w-4' />
              Supprimer l'organisation
            </button>
          </div>
        )}
      </aside>

      {/* Delete org confirmation */}
      <ConfirmModal
        isOpen={confirmDelete}
        title="Supprimer l'organisation"
        message={`Voulez-vous vraiment supprimer "${org?.name}" ? Cette action retire l'organisation de la plateforme.`}
        confirmLabel='Supprimer'
        isLoading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />

      {/* Remove admin confirmation */}
      <RemoveAdminConfirm orgId={orgId} target={adminToRemove} onDone={setAdminToRemove} onChanged={invalidate} />

      {/* Detach club confirmation */}
      <DetachClubConfirm orgId={orgId} target={clubToDetach} onDone={setClubToDetach} onChanged={invalidate} />
    </>
  );
}

// ─── Infos tab ─────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='flex items-center justify-between gap-3 py-2'>
      <span className='text-sm text-slate-500'>{label}</span>
      <span className='text-right text-sm font-semibold text-slate-900'>{children}</span>
    </div>
  );
}

function InfosTab({ org }: { org: Organization }) {
  const sub = subStatusLabel(org.subscriptionStatus);
  const connect = connectLabel(org);
  return (
    <div className='space-y-6'>
      <CmsOfferControl ownerType='ORGANIZATION' ownerId={org.id} ownerName={org.name} enabled={org.offerCms ?? false} />
      <div className='grid grid-cols-2 gap-3'>
        <div className='rounded-xl border border-slate-200 bg-slate-50/60 p-4'>
          <div className='flex items-center gap-2 text-slate-500'>
            <Briefcase className='h-4 w-4' />
            <span className='text-xs font-semibold uppercase'>Concours</span>
          </div>
          <p className='mt-1 text-2xl font-bold text-slate-900'>{org._count?.competitions ?? 0}</p>
        </div>
        <div className='rounded-xl border border-slate-200 bg-slate-50/60 p-4'>
          <div className='flex items-center gap-2 text-slate-500'>
            <Building className='h-4 w-4' />
            <span className='text-xs font-semibold uppercase'>Clubs liés</span>
          </div>
          <p className='mt-1 text-2xl font-bold text-slate-900'>{org._count?.clubMembership ?? 0}</p>
        </div>
      </div>

      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <CreditCard className='h-4 w-4 text-slate-400' /> Abonnement
        </h3>
        <InfoRow label='Plan'>
          <span className={org.plan === SubPlan.FORFAIT ? 'text-amber-700' : 'text-slate-600'}>
            {org.plan === SubPlan.FORFAIT ? 'Forfait' : 'Commission'}
          </span>
        </InfoRow>
        <InfoRow label='Statut'>
          <span className={`rounded-full px-2 py-0.5 text-xs ${sub.cls}`}>{sub.label}</span>
        </InfoRow>
        <InfoRow label='Crédit commission'>
          {(org.commissionCreditRemainingCents / 100).toFixed(2).replace('.', ',')} €
        </InfoRow>
        {org.subscriptionEndDate && (
          <InfoRow label='Échéance'>{formatDateAbbreviatedFr(org.subscriptionEndDate)}</InfoRow>
        )}
      </div>

      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <ShieldCheck className='h-4 w-4 text-slate-400' /> Stripe Connect
        </h3>
        <InfoRow label='Statut'>
          {connect ? (
            <span className={`rounded-full px-2 py-0.5 text-xs ${connect.cls}`}>{connect.label}</span>
          ) : (
            <span className='text-slate-400'>Non configuré</span>
          )}
        </InfoRow>
        <InfoRow label='SIREN'>{org.siren ?? '—'}</InfoRow>
      </div>

      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 text-sm font-bold text-slate-900'>Coordonnées</h3>
        <InfoRow label='Email'>{org.email ?? '—'}</InfoRow>
        <InfoRow label='Téléphone'>{org.phone ?? '—'}</InfoRow>
        <InfoRow label='Ville'>{[org.city, org.department].filter(Boolean).join(', ') || '—'}</InfoRow>
        <InfoRow label='Inscrit le'>{formatDateAbbreviatedFr(org.createdAt)}</InfoRow>
      </div>
    </div>
  );
}

// ─── Edit tab ──────────────────────────────────────────────────────

const FIELD_CLS =
  'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none';

function EditTab({ org, onSaved }: { org: Organization; onSaved: () => void }) {
  const [form, setForm] = useState<EditForm>(() => toEditForm(org));

  const mutation = useMutation({
    mutationFn: (payload: EditForm) =>
      updateOrganizationApi(org.id, {
        name: payload.name.trim(),
        email: payload.email.trim() || undefined,
        phone: payload.phone.trim() || undefined,
        website: payload.website.trim() || undefined,
        address: payload.address.trim() || undefined,
        city: payload.city.trim() || undefined,
        postcode: payload.postcode.trim() || undefined,
        department: payload.department.trim() || undefined,
        region: payload.region.trim() || undefined,
        siren: payload.siren.trim() || undefined,
        isProfilePublic: payload.isProfilePublic,
      }),
    onSuccess: () => {
      toast.success('Organisation mise à jour');
      onSaved();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors de la mise à jour'),
  });

  function set<K extends keyof EditForm>(key: K, value: EditForm[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function field(label: string, key: keyof EditForm, type = 'text') {
    return (
      <div>
        <label className='mb-1 block text-xs font-semibold text-slate-600'>{label}</label>
        <input
          type={type}
          value={form[key] as string}
          onChange={e => set(key, e.target.value as EditForm[typeof key])}
          className={FIELD_CLS}
        />
      </div>
    );
  }

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (!form.name.trim()) {
          toast.error('Le nom est requis');
          return;
        }
        mutation.mutate(form);
      }}
      className='space-y-4'
    >
      {field('Nom', 'name')}
      <div className='grid grid-cols-2 gap-3'>
        {field('Email', 'email', 'email')}
        {field('Téléphone', 'phone')}
      </div>
      {field('Site web', 'website')}
      {field('Adresse', 'address')}
      <div className='grid grid-cols-2 gap-3'>
        {field('Ville', 'city')}
        {field('Code postal', 'postcode')}
      </div>
      <div className='grid grid-cols-2 gap-3'>
        {field('Département', 'department')}
        {field('Région', 'region')}
      </div>
      {field('SIREN', 'siren')}

      <label className='flex items-center gap-2 text-sm text-slate-700'>
        <input
          type='checkbox'
          checked={form.isProfilePublic}
          onChange={e => set('isProfilePublic', e.target.checked)}
          className='h-4 w-4 rounded border-slate-300'
        />
        Profil public visible
      </label>

      <button
        type='submit'
        disabled={mutation.isPending}
        className='w-full rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-amber-700 disabled:opacity-60'
      >
        {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </form>
  );
}

// ─── Admins tab ────────────────────────────────────────────────────

function AdminsTab({
  org,
  onChanged,
  onAskRemove,
}: {
  org: Organization;
  onChanged: () => void;
  onAskRemove: (t: { userId: number; name: string }) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const existingIds = useMemo(() => new Set((org.admins ?? []).map(a => a.user.id)), [org.admins]);

  const { data: results, isFetching } = useQuery({
    queryKey: ['admin', 'user-search', debounced],
    queryFn: () => getUsersApi({ search: debounced, limit: 8 }),
    enabled: debounced.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: (userId: number) => addOrgAdminApi(org.id, { userId, role: OrgAdminRole.ADMIN }),
    onSuccess: () => {
      toast.success('Admin ajouté');
      setSearch('');
      setDebounced('');
      onChanged();
    },
    onError: err => toast.error(err instanceof Error ? err.message : "Erreur lors de l'ajout"),
  });

  function handleSearch(value: string) {
    setSearch(value);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setDebounced(value.trim()), 350);
  }

  const candidates = (results?.data ?? []).filter(u => !existingIds.has(u.id));

  return (
    <div className='space-y-5'>
      {/* Existing admins */}
      <div className='space-y-2'>
        {(org.admins ?? []).length === 0 ? (
          <p className='text-sm text-slate-400'>Aucun admin.</p>
        ) : (
          (org.admins ?? []).map(a => (
            <div key={a.id} className='flex items-center gap-3 rounded-lg border border-slate-200 p-3'>
              <div className='flex h-9 w-9 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-600'>
                {getNameInitials(`${a.user.firstName ?? ''} ${a.user.lastName ?? ''}`.trim() || a.user.email)}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-semibold text-slate-900'>
                  {a.user.firstName} {a.user.lastName}
                </p>
                <p className='truncate text-xs text-slate-400'>{a.user.email}</p>
              </div>
              <span className='rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600'>
                {ORG_ADMIN_ROLE_LABELS[a.role]}
              </span>
              {a.role !== OrgAdminRole.OWNER && (
                <button
                  onClick={() =>
                    onAskRemove({
                      userId: a.user.id,
                      name: `${a.user.firstName ?? ''} ${a.user.lastName ?? ''}`.trim() || a.user.email,
                    })
                  }
                  className='rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600'
                  title='Retirer'
                >
                  <Trash2 className='h-4 w-4' />
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add admin */}
      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <UserPlus className='h-4 w-4 text-slate-400' /> Ajouter un admin
        </h3>
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder='Rechercher par nom, email…'
            className='w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-amber-500 focus:outline-none'
          />
        </div>
        {debounced.length >= 2 && (
          <div className='mt-2 space-y-1'>
            {isFetching ? (
              <p className='py-2 text-center text-xs text-slate-400'>Recherche…</p>
            ) : candidates.length === 0 ? (
              <p className='py-2 text-center text-xs text-slate-400'>Aucun utilisateur disponible</p>
            ) : (
              candidates.map((u: AuthUser) => (
                <button
                  key={u.id}
                  onClick={() => addMutation.mutate(u.id)}
                  disabled={addMutation.isPending}
                  className='flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition hover:bg-amber-50 disabled:opacity-50'
                >
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm text-slate-900'>
                      {u.firstName} {u.lastName}
                    </p>
                    <p className='truncate text-xs text-slate-400'>{u.email}</p>
                  </div>
                  <UserPlus className='h-4 w-4 text-amber-600' />
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RemoveAdminConfirm({
  orgId,
  target,
  onDone,
  onChanged,
}: {
  orgId: number | null;
  target: { userId: number; name: string } | null;
  onDone: (t: null) => void;
  onChanged: () => void;
}) {
  const mutation = useMutation({
    mutationFn: () => removeOrgAdminApi(orgId as number, target!.userId),
    onSuccess: () => {
      toast.success('Admin retiré');
      onDone(null);
      onChanged();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors du retrait'),
  });

  return (
    <ConfirmModal
      isOpen={target !== null}
      title="Retirer l'admin"
      message={`Retirer ${target?.name} des admins de cette organisation ?`}
      confirmLabel='Retirer'
      isLoading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onCancel={() => onDone(null)}
    />
  );
}

// ─── Clubs tab ─────────────────────────────────────────────────────

function ClubsTab({
  org,
  onChanged,
  onAskDetach,
}: {
  org: Organization;
  onChanged: () => void;
  onAskDetach: (t: { id: number; name: string }) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data: attached } = useQuery({
    queryKey: ['admin', 'org-clubs', org.id],
    queryFn: () => getClubsApi({ organizationId: org.id, limit: 100 }),
  });
  const attachedClubs = useMemo<Club[]>(() => attached?.data ?? [], [attached]);
  const attachedIds = useMemo(() => new Set(attachedClubs.map(c => c.id)), [attachedClubs]);

  const { data: searchResults, isFetching } = useQuery({
    queryKey: ['admin', 'club-search', debounced],
    queryFn: () => getClubsApi({ search: debounced, limit: 8 }),
    enabled: debounced.length >= 2,
  });

  const attachMutation = useMutation({
    mutationFn: (clubId: number) => attachClubApi(org.id, clubId),
    onSuccess: () => {
      toast.success('Club rattaché');
      setSearch('');
      setDebounced('');
      onChanged();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors du rattachement'),
  });

  function handleSearch(value: string) {
    setSearch(value);
    clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setDebounced(value.trim()), 350);
  }

  const candidates = (searchResults?.data ?? []).filter(c => !attachedIds.has(c.id));

  return (
    <div className='space-y-5'>
      <div className='space-y-2'>
        {attachedClubs.length === 0 ? (
          <p className='text-sm text-slate-400'>Aucun club rattaché.</p>
        ) : (
          attachedClubs.map(c => (
            <div key={c.id} className='flex items-center gap-3 rounded-lg border border-slate-200 p-3'>
              <div className='flex h-9 w-9 items-center justify-center rounded-full bg-purple-100 text-xs font-bold text-purple-600'>
                {getNameInitials(c.name)}
              </div>
              <div className='min-w-0 flex-1'>
                <p className='truncate text-sm font-semibold text-slate-900'>{c.name}</p>
                <p className='truncate text-xs text-slate-400'>{[c.city, c.department].filter(Boolean).join(', ')}</p>
              </div>
              <button
                onClick={() => onAskDetach({ id: c.id, name: c.name })}
                className='rounded-lg p-1.5 text-slate-400 transition hover:bg-red-50 hover:text-red-600'
                title='Détacher'
              >
                <Trash2 className='h-4 w-4' />
              </button>
            </div>
          ))
        )}
      </div>

      <div className='rounded-xl border border-slate-200 p-4'>
        <h3 className='mb-2 flex items-center gap-2 text-sm font-bold text-slate-900'>
          <Building className='h-4 w-4 text-slate-400' /> Rattacher un club
        </h3>
        <div className='relative'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
          <input
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder='Rechercher un club…'
            className='w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-amber-500 focus:outline-none'
          />
        </div>
        {debounced.length >= 2 && (
          <div className='mt-2 space-y-1'>
            {isFetching ? (
              <p className='py-2 text-center text-xs text-slate-400'>Recherche…</p>
            ) : candidates.length === 0 ? (
              <p className='py-2 text-center text-xs text-slate-400'>Aucun club disponible</p>
            ) : (
              candidates.map(c => (
                <button
                  key={c.id}
                  onClick={() => attachMutation.mutate(c.id)}
                  disabled={attachMutation.isPending}
                  className='flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition hover:bg-amber-50 disabled:opacity-50'
                >
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm text-slate-900'>{c.name}</p>
                    <p className='truncate text-xs text-slate-400'>
                      {[c.city, c.department].filter(Boolean).join(', ')}
                    </p>
                  </div>
                  <Building className='h-4 w-4 text-amber-600' />
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DetachClubConfirm({
  orgId,
  target,
  onDone,
  onChanged,
}: {
  orgId: number | null;
  target: { id: number; name: string } | null;
  onDone: (t: null) => void;
  onChanged: () => void;
}) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => detachClubApi(orgId as number, target!.id),
    onSuccess: () => {
      toast.success('Club détaché');
      onDone(null);
      onChanged();
      if (orgId) void qc.invalidateQueries({ queryKey: ['admin', 'org-clubs', orgId] });
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur lors du détachement'),
  });

  return (
    <ConfirmModal
      isOpen={target !== null}
      title='Détacher le club'
      message={`Détacher "${target?.name}" de cette organisation ?`}
      confirmLabel='Détacher'
      isLoading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
      onCancel={() => onDone(null)}
    />
  );
}

// ─── Requests tab ──────────────────────────────────────────────────

function RequestsTab({ orgId, onAdminChanged }: { orgId: number; onAdminChanged: () => void }) {
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectedReason, setRejectedReason] = useState('');
  const [showPing, setShowPing] = useState(false);
  const [pingSubject, setPingSubject] = useState('');
  const [pingMessage, setPingMessage] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.organizations.adminRequests({
      organizationId: orgId,
      status: OrganizationAdminRequestStatus.PENDING,
    }),
    queryFn: () =>
      getOrgAdminRequestsApi({ organizationId: orgId, status: OrganizationAdminRequestStatus.PENDING, limit: 50 }),
  });

  const requests = useMemo<OrganizationAdminRequest[]>(() => data?.data ?? [], [data]);

  function invalidateRequests() {
    void qc.invalidateQueries({ queryKey: ['organizations', 'admin-requests'] });
  }

  const updateMutation = useMutation({
    mutationFn: (vars: { id: number; status: OrganizationAdminRequestStatus; rejectedReason?: string }) =>
      updateOrgAdminRequestApi(vars.id, { status: vars.status, rejectedReason: vars.rejectedReason }),
    onSuccess: (_, vars) => {
      toast.success(vars.status === OrganizationAdminRequestStatus.ACCEPTED ? 'Demande acceptée' : 'Demande rejetée');
      setRejectingId(null);
      setRejectedReason('');
      invalidateRequests();
      if (vars.status === OrganizationAdminRequestStatus.ACCEPTED) onAdminChanged();
    },
    onError: err => toast.error(err instanceof Error ? err.message : 'Erreur'),
  });

  const pingMutation = useMutation({
    mutationFn: () => pingOrgAdminsApi(orgId, { subject: pingSubject.trim(), message: pingMessage.trim() }),
    onSuccess: res => {
      toast.success(`Email envoyé à ${res.sent} admin(s)`);
      setShowPing(false);
      setPingSubject('');
      setPingMessage('');
    },
    onError: err => toast.error(err instanceof Error ? err.message : "Erreur d'envoi"),
  });

  if (isLoading) {
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2 className='h-5 w-5 animate-spin text-slate-400' />
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <h3 className='text-sm font-bold text-slate-900'>Demandes en attente ({requests.length})</h3>

      {requests.length === 0 ? (
        <p className='text-sm text-slate-400'>Aucune demande en attente.</p>
      ) : (
        requests.map(req => {
          const name =
            [req.requester?.firstName, req.requester?.lastName].filter(Boolean).join(' ') || req.requester?.email || '';
          return (
            <div key={req.id} className='space-y-2 rounded-lg border border-slate-200 p-3'>
              <div className='flex items-start gap-3'>
                <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-600'>
                  {getNameInitials(name)}
                </div>
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-semibold text-slate-900'>{name}</p>
                  <p className='truncate text-xs text-slate-400'>{req.requester?.email}</p>
                  {req.message && <p className='mt-1 text-xs italic text-slate-600'>"{req.message}"</p>}
                  <p className='text-xs text-slate-400'>{formatDateAbbreviatedFr(req.createdAt)}</p>
                </div>
              </div>

              {rejectingId === req.id ? (
                <div className='space-y-2 pt-1'>
                  <input
                    value={rejectedReason}
                    onChange={e => setRejectedReason(e.target.value)}
                    placeholder='Motif du refus (optionnel, 500 car. max)'
                    maxLength={500}
                    className='w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-amber-500 focus:outline-none'
                  />
                  <div className='flex gap-2'>
                    <button
                      onClick={() =>
                        updateMutation.mutate({
                          id: req.id,
                          status: OrganizationAdminRequestStatus.REJECTED,
                          rejectedReason: rejectedReason.trim() || undefined,
                        })
                      }
                      disabled={updateMutation.isPending}
                      className='flex-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-60'
                    >
                      Confirmer le refus
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(null);
                        setRejectedReason('');
                      }}
                      className='rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50'
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className='flex gap-2 pt-1'>
                  <button
                    onClick={() =>
                      updateMutation.mutate({ id: req.id, status: OrganizationAdminRequestStatus.ACCEPTED })
                    }
                    disabled={updateMutation.isPending}
                    className='flex-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60'
                  >
                    Accepter
                  </button>
                  <button
                    onClick={() => setRejectingId(req.id)}
                    disabled={updateMutation.isPending}
                    className='flex-1 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-60'
                  >
                    Rejeter
                  </button>
                </div>
              )}
            </div>
          );
        })
      )}

      {/* Ping admins */}
      <div className='rounded-xl border border-slate-200 p-4'>
        <button
          onClick={() => setShowPing(v => !v)}
          className='flex w-full items-center gap-2 text-sm font-bold text-slate-900'
        >
          <Bell className='h-4 w-4 text-slate-400' />
          Contacter les admins par email
          <span className='ml-auto text-xs text-slate-400'>{showPing ? '▲' : '▼'}</span>
        </button>

        {showPing && (
          <form
            onSubmit={e => {
              e.preventDefault();
              if (!pingSubject.trim() || !pingMessage.trim()) return;
              pingMutation.mutate();
            }}
            className='mt-3 space-y-2'
          >
            <input
              value={pingSubject}
              onChange={e => setPingSubject(e.target.value)}
              placeholder='Sujet (3–200 car.)'
              minLength={3}
              maxLength={200}
              required
              className='w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none'
            />
            <textarea
              value={pingMessage}
              onChange={e => setPingMessage(e.target.value)}
              placeholder='Message (10–2000 car.)'
              minLength={10}
              maxLength={2000}
              required
              rows={4}
              className='w-full resize-none rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none'
            />
            <button
              type='submit'
              disabled={pingMutation.isPending || !pingSubject.trim() || !pingMessage.trim()}
              className='w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-60'
            >
              {pingMutation.isPending ? 'Envoi…' : 'Envoyer'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
